import React, { useState, useEffect, useRef } from 'react';
import { EventAnalysisDashboard } from './components/EventAnalysisDashboard.tsx';
import { DashboardHeader } from './components/DashboardHeader.tsx';
import { getSession } from './services/apiService.ts';
import DataTransformer from './utilsFunction/dataTransformer.ts';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { executeAndWaitForCompletion } from './services/apiService.ts';
import { uploadInvoiceFiles } from './services/benchmarking_file_upload_api.ts';

interface AppState {
  loading: boolean;
  error: string | null;
  sessionData: any;
  transformedData: {
    tab1Data: any;
    tab2Data: any;
    tabs: string[];
    vendorTotalsFromSummary: any;
  } | null;
  benchmarkData: any | null;  
  activeTab: string;
}

const App: React.FC = () => {
  const [state, setState] = useState<AppState>({
    loading: true,
    error: null,
    sessionData: null,
    transformedData: null,
    benchmarkData: null,  
    activeTab: 'Cost Benchmarking'
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runResponse, setRunResponse] = useState<any>(null);
  const hasFetchedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ---------- FILE / RUN STATE ---------- */
const [selectedQuotationFile,  setSelectedQuotationFile]  = useState<File | null>(null);
const [selectedQuotationFiles, setSelectedQuotationFiles] = useState<File[]>([]);
const [selectedBenchmarkFile,  setSelectedBenchmarkFile]  = useState<File | null>(null);

const [isRunningQuotation,  setIsRunningQuotation]  = useState(false);
const [isRunningBenchmark,  setIsRunningBenchmark]  = useState(false);

const [quotationRunError,  setQuotationRunError]  = useState<string | null>(null);
const [benchmarkRunError,  setBenchmarkRunError]  = useState<string | null>(null);

/* ---------- INPUT REFS ---------- */
const quotationFileInputRef = useRef<HTMLInputElement>(null);
const benchmarkFileInputRef = useRef<HTMLInputElement>(null);


const handleQuotationFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  setSelectedQuotationFile(e.target.files?.[0] || null);
  
  if (e.target.files && e.target.files.length > 0) {
    const filesArray = Array.from(e.target.files);
    setSelectedQuotationFiles(filesArray);
  } else {
    setSelectedQuotationFiles([]);
  }
};

const handleBenchmarkFileChange  = (e: React.ChangeEvent<HTMLInputElement>) =>
  setSelectedBenchmarkFile(e.target.files?.[0] || null);

const openQuotationFilePicker  = () => quotationFileInputRef.current?.click();
const openBenchmarkFilePicker  = () => benchmarkFileInputRef.current?.click();



  // Fetch data from API (only on mount and retry, not after run)
  const fetchData = async () => {
    if (isRunning || hasFetchedRef.current) return; // Prevent fetch during/after run or double fetch
    hasFetchedRef.current = true;
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const sessionData = await getSession();
      const transformedDataRaw = DataTransformer.transformSessionData(sessionData);
      const backendTabs: string[] = transformedDataRaw.tabs || [];
      const transformedData = {
        ...transformedDataRaw,
        tabs: backendTabs
      };

      setState(prev => ({
        ...prev,
        loading: false,
        error: null,
        sessionData: sessionData,
        transformedData: transformedData,
        activeTab: backendTabs.includes(prev.activeTab) ? prev.activeTab : (backendTabs[0] || 'Cost Benchmarking')
      }));
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: `Unable to load dashboard data: ${error?.message || error}`,
        transformedData: null,
        sessionData: null
      }));
    }
  };

  // Load data on component mount
  useEffect(() => {
    // Check if we have insights data from URL parameters (from DocumentUploader)
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get('data');
    
    if (dataParam) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(dataParam));
        const { sessionData, transformedData } = decodedData;
        
        setState(prev => ({
          ...prev,
          loading: false,
          error: null,
          sessionData: sessionData,
          transformedData: transformedData,
          activeTab: transformedData.tabs[0] || 'Vendor to Vendor'
        }));
        
        // Clean up URL by removing the data parameter
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        
        return; // Don't fetch from API if we have insights data
      } catch (error) {
        console.error('Error parsing insights data from URL:', error);
        // Clean up URL even if parsing failed
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
    }
    
    // If no insights data, fetch from API
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle tab change
  const handleTabChange = (tab: string) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  };

  // Retry function
  const handleRetry = () => {
    if (!isRunning) fetchData();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };


  // const handleRun = async () => {
  //   // if (!selectedFile) return;
  //   // setIsRunning(true);
  //   // setRunError(null);
  //   // setRunResponse(null);

  // if (!selectedQuotationFile) return;
  // setIsRunningQuotation(true);
  // setRunError(null);
  // setQuotationRunError(null);
  // setRunResponse(null);
  //   let errorDuringSSE = false;
  //   try {
  //     const result = await executeAndWaitForCompletion(
  //       selectedQuotationFile,
  //       {},
  //       (msg) => {
  //         if (msg.status === 'failed' || msg.status === 'error') {
  //           setIsRunningQuotation(false);
  //           setQuotationRunError(msg.message || 'Mind execution failed.');
  //           errorDuringSSE = true;
  //         }
  //       },
  //       (sessionData) => {
  //         // Update state with new session data and transformed data
  //         const transformedDataRaw = DataTransformer.transformSessionData(sessionData);
  //         const allowedTabs = ['Vendor to Vendor', 'Vendor to Benchmark'];
  //         const filteredTabs = transformedDataRaw.tabs.filter((tab) => allowedTabs.includes(tab));
  //         const transformedData = {
  //           ...transformedDataRaw,
  //           tabs: filteredTabs
  //         };
  //         setState((prev) => ({
  //           ...prev,
  //           loading: false,
  //           error: null,
  //           sessionData: sessionData,
  //           transformedData: transformedData,
  //           activeTab: allowedTabs.includes(prev.activeTab) ? prev.activeTab : filteredTabs[0] || allowedTabs[0]
  //         }));
  //         setIsRunningQuotation(false);
  //       }
  //     );
  //     // No-op: all state updates are handled in the SSE callback
  //     // Optionally, you can refresh dashboard data here if needed
  //   } catch (err: any) {
  //     setQuotationRunError(err.message || 'Failed to run mind');
  //     setIsRunningQuotation(false);
  //   }
  // };

  const handleRun = async () => {
    // Create an array of files to process    
    const filesToProcess = selectedQuotationFiles.length > 0 
      ? selectedQuotationFiles 
      : selectedQuotationFile 
        ? [selectedQuotationFile] 
        : [];
    
    if (filesToProcess.length === 0) return;
    
    setIsRunningQuotation(true);
    setRunError(null);
    setQuotationRunError(null);
    setRunResponse(null);
    
    let errorDuringSSE = false;
    try {
      const result = await executeAndWaitForCompletion(
        filesToProcess,  // Pass array of files to the API
        {},
        (msg) => {
          if (msg.status === 'failed' || msg.status === 'error') {
            setIsRunningQuotation(false);
            setQuotationRunError(msg.message || 'Mind execution failed.');
            errorDuringSSE = true;
          }
        },
        (sessionData) => {
          // Update state with new session data and transformed data
          const transformedDataRaw = DataTransformer.transformSessionData(sessionData);
          const backendTabs: string[] = transformedDataRaw.tabs || [];
          const transformedData = {
            ...transformedDataRaw,
            tabs: backendTabs
          };
          setState((prev) => ({
            ...prev,
            loading: false,
            error: null,
            sessionData: sessionData,
            transformedData: transformedData,
            activeTab: backendTabs.includes(prev.activeTab) ? prev.activeTab : (backendTabs[0] || 'Cost Benchmarking')
          }));
          setIsRunningQuotation(false);
        }
      );
      // No-op: all state updates are handled in the SSE callback
    } catch (err: any) {
      setQuotationRunError(err.message || 'Failed to run mind');
      setIsRunningQuotation(false);
    }
  };

  function tryParseJsonString(raw: unknown): any | null {
    if (typeof raw !== 'string') return null;
  
    let txt = raw.trim();
  
    // Remove leading and trailing ```
    if (txt.startsWith('```')) {
      txt = txt
        .replace(/^```/, '')             // <-- This was previously broken
        .replace(/```\s*$/, '')          // <-- This was fine
        .trim();
    }
  
    try {
      return JSON.parse(txt);
    } catch {
      return null;
    }
  }  

/* ---------- BENCHMARK (uses uploadInvoiceFiles) ---------- */
const handleRunBenchmark = async () => {
  if (!selectedBenchmarkFile) return;

  setIsRunningBenchmark(true);
  setBenchmarkRunError(null);

  try {
    /* 1. upload */
    const raw = await uploadInvoiceFiles(selectedBenchmarkFile);

    /* 2. extract DataRead_001 tab */
    const dataReadContent = extractBenchmarkTabs(raw);

    /* 3. update state */
    setState(prev => ({
      ...prev,
      benchmarkData: dataReadContent,
      transformedData: null,
      sessionData: null,
      loading: false,
      error: null,
      activeTab: 'Cost Benchmarking'
    }));
    
    if (!dataReadContent) {
      setBenchmarkRunError('Could not find DataRead_001 tab in the response. The file may have a different structure than expected.');
    }
  } catch (e: any) {
    setBenchmarkRunError(e.message || 'Benchmark upload failed');
  } finally {
    setIsRunningBenchmark(false);
  }
};


  // Loading state
  // if (state.loading) {
  //   return (
  //     <div className="min-h-screen bg-primary-50 flex items-center justify-center">
  //       <div className="bg-white rounded-xl shadow-soft p-8 max-w-md w-full mx-4">
  //         <div className="text-center">
  //           <Loader className="w-12 h-12 text-accent-600 animate-spin mx-auto mb-4" />
  //           <h2 className="text-xl font-semibold text-primary-900 mb-2">Loading Analysis</h2>
  //           <p className="text-primary-600 mb-6">Fetching session data and preparing dashboard...</p>
  //           <div className="w-full bg-primary-200 rounded-full h-2">
  //             <div className="bg-accent-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // BenchmarkJsonViewer
//  const BenchmarkJsonViewer: React.FC<{ data: any }> = ({ data }) => (
//    <div className="bg-white rounded-xl shadow-soft p-6 overflow-auto">
//      {/* <div>{data}</div> */}
//     <h3 className="text-lg font-semibold mb-4">Benchmark file output</h3>
//     <pre className="text-xs whitespace-pre-wrap break-all font-mono leading-relaxed max-w-full">
//       {JSON.stringify(data, null, 2)}
//     </pre>
//   </div>
// );


const BenchmarkJsonViewer: React.FC<{ data: any }> = ({ data }) => {
  const [isJsonVisible, setIsJsonVisible] = useState(true);
  // Create a simple formatted display of cost breakdown sections
  // const renderCostBreakdown = () => {
  //   if (!data?.costBreakdown || !Array.isArray(data.costBreakdown)) {
  //     return <p>No cost breakdown data available</p>;
  //   }

  //   return (
  //     <div className="mt-4">
  //       <h4 className="text-md font-medium mb-2">Cost Categories</h4>
  //       {data.costBreakdown.map((category, idx) => (
  //         <div key={idx} className="mb-4 border-l-2 border-accent-300 pl-3">
  //           <div className="font-medium">{category.mainCategory}: {category.subCategory}</div>
  //           <div className="text-xs text-gray-600 mt-1">
  //             {category.items.length} items - Total: ₹{category.items.reduce((sum: number, item: any) => sum + (item.totalCostNumeric || 0), 0).toLocaleString()}
  //           </div>
  //         </div>
  //       ))}
        
  //       {data.costSummary && (
  //         <div className="mt-4 bg-accent-50 p-3 rounded">
  //           <div className="text-sm font-bold">Total Cost: ₹{data.costSummary.totalNumericCost.toLocaleString()}</div>
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6 overflow-auto">
      <h3 className="text-lg font-semibold mb-4">Benchmark Data</h3>
      
      {data ? (
        <div>
          {/* Client and Project Info */}
          {/* {data.clientInformation && (
            <div className="mb-4">
              <h4 className="text-md font-medium mb-2">Client Information</h4>
              <div className="text-sm">
                <div><span className="font-medium">Company:</span> {data.clientInformation.companyName}</div>
                <div><span className="font-medium">Contact:</span> {data.clientInformation.contactPerson}</div>
                <div><span className="font-medium">Email:</span> {data.clientInformation.email}</div>
              </div>
            </div>
          )}
          
          {data.projectInformation && (
            <div className="mb-4">
              <h4 className="text-md font-medium mb-2">Project Details</h4>
              <div className="text-sm">
                <div><span className="font-medium">Event:</span> {data.projectInformation.projectName}</div>
                <div><span className="font-medium">Location:</span> {data.projectInformation.eventLocation}</div>
                <div><span className="font-medium">Date:</span> {data.projectInformation.eventDate}</div>
                <div><span className="font-medium">Attendees:</span> {data.projectInformation.expectedAttendees}</div>
              </div>
            </div>
          )} */}
          
          {/* Cost Breakdown */}
          {/* {renderCostBreakdown()} */}
          
          {/* View Full Data Link */}
          {/* <details className="mt-6">
            <summary className="cursor-pointer text-sm text-accent-600 hover:text-accent-800">
              Collapse JSON Data
            </summary>
            <pre className="mt-2 text-xs whitespace-pre-wrap break-all font-mono leading-relaxed max-w-full bg-gray-50 p-3 rounded">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details> */}
          <div className="mt-6">
            <button 
              onClick={() => setIsJsonVisible(!isJsonVisible)}
              className="cursor-pointer text-sm text-accent-600 hover:text-accent-800 bg-transparent border-none flex items-center"
            >
              {isJsonVisible ? 'Hide JSON Data' : 'View JSON Data'}
            </button>
            
            {isJsonVisible && (
              <pre className="mt-2 text-xs whitespace-pre-wrap break-all font-mono leading-relaxed max-w-full bg-gray-50 p-3 rounded">
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </div>
        </div>
      ) : (
        <p className="text-primary-500">No DataRead_001 content found in response</p>
      )}
    </div>
  );
};

function extractBenchmarkTabs(apiResp: any) {
  if (!apiResp || typeof apiResp !== 'object') return null;

  // Navigate to the correct path in the API response
  const tabContent = apiResp.response?.output?.content?.DataRead_001;
  
  if (!tabContent || !Array.isArray(tabContent) || tabContent.length === 0) {
    return null;
  }

  // Get content from the first item in the array
  const markdownContent = tabContent[0]?.content;
  
  if (!markdownContent || typeof markdownContent !== 'string') {
    return null;
  }

  // Extract JSON from markdown code block
  const jsonRegex = /```json\n([\s\S]*?)\n```/;
  const match = markdownContent.match(jsonRegex);
  
  if (!match || !match[1]) {
    return null;
  }

  try {
    // Parse the extracted JSON string
    const jsonData = JSON.parse(match[1]);
    return jsonData;
  } catch (e) {
    console.error("Failed to parse JSON from DataRead_001 content:", e);
    return null;
  }
}
  

  // Error state (but with fallback data)
  const ErrorBanner = () => (
    state.error && (
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-warning-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-medium text-warning-800">API Connection Issue</h4>
            <p className="text-sm text-warning-700 mt-1">{state.error}</p>
          </div>
          <button
            onClick={handleRetry}
            className="flex items-center gap-2 px-3 py-1 bg-warning-100 hover:bg-warning-200 text-warning-800 rounded text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    )
  );

  // initial banner 
  const InitialBanner = () => (
    !state.transformedData && !state.benchmarkData && (
      <div className="bg-white rounded-xl shadow-soft p-6 mb-8">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary-900">ABG Event Bench</h1>
          </div>
        </div>
      </div>
    )
  );   

  // Main render
  if (!state.loading && state.error) {
    return (
      <div className="min-h-screen bg-primary-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-soft p-8 max-w-md w-full mx-4 text-center">
          <AlertCircle className="w-12 h-12 text-danger-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-danger-900 mb-2">Dashboard Error</h3>
          <p className="text-danger-600 mb-4">{state.error}</p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-lg font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      <div className={`max-w-7xl mx-auto px-4 py-8 ${!(state.transformedData || state.benchmarkData) ? "initial-Layout-styles" : ""}`}>
        
        {/* Error Banner */}
        <ErrorBanner />

        <InitialBanner />
        
        {/* Quotation File input and Run button */}
        {/* <div className={`bg-white rounded-xl shadow-soft p-6 mb-8 flex flex-col md:flex-row items-center gap-4 ${!state.transformedData && "initial-fileinput-box"}`}>
         
          <label>Quotation File input</label>

      <input
        ref={fileInputRef}
        type="file"
        accept="*"
        onChange={handleFileChange}
        style={{ display: "none" }}
        disabled={isRunning}
      />
      <button
        type="button"
        className="px-4 py-2 bg-accent-50 text-accent-700 rounded-lg font-semibold text-sm hover:bg-accent-100"
        onClick={openFilePicker}
        disabled={isRunning}
      >
        Choose File
      </button>
         {selectedFile && (
            <span className="mt-2 text-xs text-gray-600">{selectedFile.name}</span>
          )}
          <button
            onClick={handleRun}
            disabled={!selectedFile || isRunning}
            className={`inline-flex items-center gap-2 px-6 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors ${isRunning ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            Run
          </button>
          {isRunning && (
            <div className="ml-4 flex items-center gap-2">
              <div className="shimmer w-32 h-4 rounded bg-primary-200" />
              <span className="text-primary-500 text-sm">Running...</span>
            </div>
          )}
        </div> */}

        {/* Benchmark File input and Run button */}
        {/* <div className={`bg-white rounded-xl shadow-soft p-6 mb-8 flex flex-col md:flex-row items-center gap-4 ${!state.transformedData && "initial-fileinput-box"}`}>
        <label>Benchmark File input</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="*"
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={isRunning}
        />
        <button
          type="button"
          className="px-4 py-2 bg-accent-50 text-accent-700 rounded-lg font-semibold text-sm hover:bg-accent-100"
          onClick={openFilePicker}
          disabled={isRunning}
        >
          Choose File
        </button>
          {selectedFile && (
              <span className="mt-2 text-xs text-gray-600">{selectedFile.name}</span>
            )}
            <button
              onClick={handleRun}
              disabled={!selectedFile || isRunning}
              className={`inline-flex items-center gap-2 px-6 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors ${isRunning ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              Run
            </button>
            {isRunning && (
              <div className="ml-4 flex items-center gap-2">
                <div className="shimmer w-32 h-4 rounded bg-primary-200" />
                <span className="text-primary-500 text-sm">Running...</span>
              </div>
            )}
        </div> */}

      {/* -----------------------------  QUOTATION  ----------------------------- */}
      <div className={`bg-white rounded-xl shadow-soft p-6 mb-8 flex flex-col md:flex-row items-center gap-4 ${!(state.transformedData || state.benchmarkData) ? "initial-fileinput-box" : ""}`}>
        <label>Quotation file</label>

        <input
          ref={quotationFileInputRef}
          type="file"
          accept="*"
          multiple
          onChange={handleQuotationFileChange}
          style={{display:'none'}}
          disabled={isRunningQuotation}
        />

        <button
          type="button"
          onClick={openQuotationFilePicker}
          disabled={isRunningQuotation}
          className="px-4 py-2 bg-accent-50 text-accent-700 rounded-lg font-semibold text-sm hover:bg-accent-100"
        >
          Choose file
        </button>

        {selectedQuotationFiles.length > 0 ? (
          <div className="mt-2 text-xs text-gray-600">
            <div className="font-medium mb-1">{selectedQuotationFiles.length} file{selectedQuotationFiles.length !== 1 ? 's' : ''} selected</div>
            <div className="max-h-20 overflow-y-auto pl-2 border-l-2 border-accent-200">
              {selectedQuotationFiles.map((file, index) => (
                <div key={index} className="truncate">{file.name} ({(file.size / 1024).toFixed(1)} KB)</div>
              ))}
            </div>
          </div>
        ) : selectedQuotationFile && (
          <span className="mt-2 text-xs text-gray-600">{selectedQuotationFile.name} ({(selectedQuotationFile.size / 1024).toFixed(1)} KB)</span>
        )}

        <button
          onClick={handleRun}
          disabled={(selectedQuotationFiles.length === 0 && !selectedQuotationFile) || isRunningQuotation}
          className={`inline-flex items-center gap-2 px-6 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors ${isRunningQuotation ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          Run
        </button>

        {isRunningQuotation && (
          <div className="ml-4 flex items-center gap-2">
            <div className="shimmer w-32 h-4 rounded bg-primary-200" />
            <span className="text-primary-500 text-sm">Running…</span>
          </div>
        )}
      </div>

      {/* -----------------------------  BENCHMARK  ----------------------------- */}
     { <div className={`bg-white rounded-xl shadow-soft p-6 mb-8 flex flex-col md:flex-row items-center gap-4 ${!(state.transformedData || state.benchmarkData) ? "initial-fileinput-box" : ""}`}>
        <label>Benchmark file</label>

        <input
          ref={benchmarkFileInputRef}
          type="file"
          accept="*"
          onChange={handleBenchmarkFileChange}
          style={{display:'none'}}
          disabled={isRunningBenchmark}
        />

        <button
          type="button"
          onClick={openBenchmarkFilePicker}
          disabled={isRunningBenchmark}
          className="px-4 py-2 bg-accent-50 text-accent-700 rounded-lg font-semibold text-sm hover:bg-accent-100"
        >
          Choose file
        </button>

        {selectedBenchmarkFile && (
          <span className="mt-2 text-xs text-gray-600">{selectedBenchmarkFile.name}</span>
        )}

        <button
          onClick={handleRunBenchmark}
          disabled={!selectedBenchmarkFile || isRunningBenchmark}
          className={`inline-flex items-center gap-2 px-6 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors ${isRunningBenchmark ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          Run
        </button>

        {isRunningBenchmark && (
          <div className="ml-4 flex items-center gap-2">
            <div className="shimmer w-32 h-4 rounded bg-primary-200" />
            <span className="text-primary-500 text-sm">Running…</span>
          </div>
        )}
      </div>}

        {/* Show error if run failed */}
        {/* {runError && (
          <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-4 text-danger-800">{runError}</div>
        )} */}

        {quotationRunError && <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-4 text-danger-800">{quotationRunError}</div>}
        {benchmarkRunError && <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-4 text-danger-800">{benchmarkRunError}</div>}

        {/* Show response if available */}
        {/* {runResponse && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 mb-4 text-success-800">
            <pre className="whitespace-pre-wrap break-all text-xs">{JSON.stringify(runResponse, null, 2)}</pre>
          </div>
        )} */}
        
        {/* Dashboard Header */}
        {state.sessionData && (
          <DashboardHeader session={state.sessionData} />
        )}
        
        {/* Tab Navigation */}
        {state.transformedData && state.transformedData.tabs.length > 0 && (
          <div className="bg-white rounded-xl shadow-soft mb-8">
            <div className="border-b border-primary-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {state.transformedData.tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`
                      whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium
                      ${state.activeTab === tab
                        ? 'border-accent-500 text-accent-600'
                        : 'border-transparent text-primary-500 hover:text-primary-700 hover:border-primary-300'
                      }
                    `}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
        
        {/* Main Dashboard */}
        {state.transformedData ? (
          <EventAnalysisDashboard
            activeTab={state.activeTab}
            sessionData={state.sessionData}
            vendorTotalsFromSummary={state.transformedData.vendorTotalsFromSummary}
            transformedData={state.transformedData}
          />
        ) :state.benchmarkData ? (
          <BenchmarkJsonViewer data={state.benchmarkData} />
        ) : (
          // <div className="bg-white rounded-xl shadow-soft p-8 text-center">
          //   <AlertCircle className="w-12 h-12 text-primary-400 mx-auto mb-4" />
          //   <h3 className="text-lg font-semibold text-primary-900 mb-2">No Data Available</h3>
          //   <p className="text-primary-600 mb-4">Unable to load dashboard data. Please check your connection and try again.</p>
          //   <button
          //     onClick={handleRetry}
          //     className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg font-medium transition-colors"
          //   >
          //     <RefreshCw className="w-4 h-4" />
          //     Retry
          //   </button>
          // </div>
          null
        )}
      </div>
    </div>
  );
};

export default App;
